import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronUp, ChevronDown, Trash2, Plus, X, Eye, Play } from "lucide-react";
import { api } from "../../lib/api";
import { Btn, TextField, SelectField, FileUpload, Label, EffSelect, TrackPicker, Loading, ErrorState, Empty, text } from "./ui";

export default function ContentManager({ col, schema }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // object or null
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(false);
    try {
      const { data } = await api.get(`/admin/content/${col}`);
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(true);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [col]);

  const startNew = () => {
    const blank = { status: "DRAFT" };
    schema.fields.forEach((f) => { blank[f.key] = f.type === "select" ? f.options[0].value : f.type === "tracks" ? [] : f.type === "number" ? 0 : ""; });
    setEditing(blank);
  };

  const save = async () => {
    try {
      if (editing.id) {
        await api.put(`/admin/content/${col}/${editing.id}`, editing);
      } else {
        await api.post(`/admin/content/${col}`, editing);
      }
      toast.success("Enregistré");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cet élément ?")) return;
    try {
      await api.delete(`/admin/content/${col}/${id}`);
      toast.success("Supprimé");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Suppression impossible"); }
  };

  const setStatus = async (item, status) => {
    try {
      await api.put(`/admin/content/${col}/${item.id}`, { status });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Mise à jour impossible"); }
  };

  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const a = items[idx], b = items[j];
    try {
      await api.put(`/admin/content/${col}/${a.id}`, { order: b.order });
      await api.put(`/admin/content/${col}/${b.id}`, { order: a.order });
      load();
    } catch (e) { toast.error("Réordonnancement impossible"); }
  };

  const set = (k) => (v) => setEditing((e) => ({ ...e, [k]: v }));

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h2 className="font-display text-3xl">{schema.title}</h2>
        <div className="flex items-center gap-3">
          <a data-testid="preview-site" href="/?preview=1" target="_blank" rel="noreferrer"
            className="text-xs tracking-widest uppercase px-5 py-2.5 border border-[#292929] hover:border-white transition-colors duration-300 inline-flex items-center gap-1.5">
            <Eye className="w-3.5 h-3.5" /> Aperçu
          </a>
          <Btn data-testid={`${col}-new`} variant="solid" onClick={startNew}>
            <Plus className="w-3.5 h-3.5 inline mr-1" /> Ajouter
          </Btn>
        </div>
      </div>

      {loading ? (
        <Loading />
      ) : error ? (
        <ErrorState onRetry={load} />
      ) : (
        <div className="border-t border-[#292929]">
          {items.length === 0 && <Empty />}
          {items.map((it, i) => (
            <div key={it.id} data-testid={`${col}-row-${i}`}
              className="border-b border-[#292929] py-4 flex items-center gap-4">
              <div className="flex flex-col">
                <button data-testid={`${col}-up-${i}`} onClick={() => move(i, -1)} className="text-muted-eff hover:text-white"><ChevronUp className="w-4 h-4" /></button>
                <button data-testid={`${col}-down-${i}`} onClick={() => move(i, 1)} className="text-muted-eff hover:text-white"><ChevronDown className="w-4 h-4" /></button>
              </div>
              <div className="flex-1 min-w-0">
                <div className="truncate">{text(it[schema.listPrimary]) || "(sans titre)"}</div>
                <div className="text-xs text-muted-eff truncate">{text(it[schema.listSecondary])}</div>
              </div>
              {col === "audio_tracks" && (
                <span data-testid={`audio_tracks-plays-${i}`} className="text-[10px] text-muted-eff tracking-widest uppercase flex items-center gap-1 whitespace-nowrap" title="Écoutes">
                  <Play className="w-3 h-3" /> {text(it.plays || 0)}
                </span>
              )}
              <EffSelect testid={`${col}-status-${i}`} size="sm" className="w-[150px]" value={it.status}
                onChange={(v) => setStatus(it, v)}
                options={[{ value: "DRAFT", label: "Brouillon" }, { value: "PUBLISHED", label: "Publié" }, { value: "UNPUBLISHED", label: "Dépublié" }]} />
              <Btn data-testid={`${col}-edit-${i}`} variant="ghost" onClick={() => setEditing(it)}>Éditer</Btn>
              <button data-testid={`${col}-delete-${i}`} onClick={() => remove(it.id)} className="text-muted-eff hover:text-white"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex justify-end">
          <div data-testid={`${col}-editor`} className="w-full max-w-xl h-full bg-black border-l border-[#292929] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-5 border-b border-[#292929] sticky top-0 bg-black z-10">
              <span className="text-xs tracking-widest uppercase text-muted-eff">{editing.id ? "Éditer" : "Nouveau"} · {schema.title}</span>
              <button onClick={() => setEditing(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-5">
              {schema.fields.map((f) => {
                if (f.type === "text") return <TextField key={f.key} label={f.label} value={editing[f.key]} onChange={set(f.key)} />;
                if (f.type === "textarea") return <TextField key={f.key} label={f.label} textarea value={editing[f.key]} onChange={set(f.key)} />;
                if (f.type === "number") return <TextField key={f.key} label={f.label} type="number" value={editing[f.key]} onChange={set(f.key)} />;
                if (f.type === "datetime") return <TextField key={f.key} label={f.label} type="datetime-local" value={editing[f.key]} onChange={set(f.key)} />;
                if (f.type === "select") return <SelectField key={f.key} label={f.label} value={editing[f.key]} onChange={set(f.key)} options={f.options} />;
                if (f.type === "file") return <FileUpload key={f.key} label={f.label} kind={f.kind} value={editing[f.key]} onChange={set(f.key)} />;
                if (f.type === "tracks") return <TrackPicker key={f.key} label={f.label} value={editing[f.key]} onChange={set(f.key)} />;
                return null;
              })}
              <div>
                <Label>Statut</Label>
                <SelectField value={editing.status} onChange={set("status")}
                  options={[{ value: "DRAFT", label: "Brouillon" }, { value: "PUBLISHED", label: "Publié" }, { value: "UNPUBLISHED", label: "Dépublié" }]} />
              </div>
              <div className="flex gap-3 pt-4">
                <Btn data-testid={`${col}-save`} variant="solid" onClick={save}>Enregistrer</Btn>
                <Btn variant="ghost" onClick={() => setEditing(null)}>Annuler</Btn>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
